import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Gift, MessageCircle, Share2 } from 'lucide-react';
import { referralLink } from '@/lib/referral';
import { studioById, type StudioId } from '@/lib/studios';
import { useMyReferral, useReferralTerms, useSignedIn } from './useReel';

// THE SHARE CARD (blueprint section 04, 2026-10-16). Under every delivered
// file, and behind Share on the Reel: one link back to the stage that made
// it, carrying the sharer's referral code when they are signed in. On phones
// Share hands over the file itself with the link in its text, so the video
// that travels still points home.

export interface ShareCardProps {
  studio: StudioId;
  /** The page the link opens; the studio's own page when not given (a quick tool passes its path). */
  path?: string;
  /** A Reel item: the link opens that output on /watch. */
  itemId?: string;
  /** The finished file, shared as a file where the phone allows it. */
  mediaUrl?: string;
  fileName?: string;
  className?: string;
  /** Compact: no heading (inside the Reel's own panel). */
  compact?: boolean;
  /** 'storybook' on the cream storybook pages; dark studio otherwise. */
  tone?: 'studio' | 'storybook';
}

function origin() {
  return typeof window !== 'undefined' ? window.location.origin : 'https://www.faceshot-chopshop.com';
}

export function useShareLink(studio: StudioId, { itemId, path }: { itemId?: string; path?: string } = {}) {
  const my = useMyReferral();
  const code = my.data?.enabled ? my.data.code : null;
  const to = itemId ? `/watch?item=${encodeURIComponent(itemId)}` : path || studioById(studio).path('en');
  return referralLink(origin(), to, code);
}

async function fileFrom(url: string, name: string): Promise<File | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    // Big files make share sheets stall; past 50 MB the link alone goes.
    if (blob.size > 50 * 1024 * 1024) return null;
    return new File([blob], name, { type: blob.type || 'video/mp4' });
  } catch {
    return null;
  }
}

export function ShareCard({ studio, path, itemId, mediaUrl, fileName, className = '', compact = false, tone = 'studio' }: ShareCardProps) {
  const { t } = useTranslation();
  const isAuthenticated = useSignedIn();
  const terms = useReferralTerms();
  const link = useShareLink(studio, { itemId, path });
  const [copied, setCopied] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const text = t('reel.share.text', { studio: t(`studios.${studio}.name`) });
  const credits = terms.data?.enabled ? terms.data.credits : 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t('reel.share.copyPrompt'), link);
    }
  };

  const share = async () => {
    if (typeof navigator.share !== 'function') return copy();
    setBusy(true);
    try {
      const file = mediaUrl ? await fileFrom(mediaUrl, fileName || 'faceshot.mp4') : null;
      const withFile = file && navigator.canShare?.({ files: [file] }) ? { files: [file] } : {};
      await navigator.share({ title: 'FaceShot', text: `${text} ${link}`, url: link, ...withFile });
    } catch {
      /* closed the share sheet */
    } finally {
      setBusy(false);
    }
  };

  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`;
  const light = tone === 'storybook';
  const btn = `inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-full border px-3 text-sm font-semibold text-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${light ? 'border-border bg-card hover:border-primary/60' : 'border-white/15 bg-white/[0.06] hover:border-accent/60 hover:bg-accent/10'}`;

  return (
    <section
      data-testid="share-card"
      aria-label={t('reel.share.title')}
      className={`relative overflow-hidden rounded-[1.35rem] border p-4 sm:p-5 ${light ? 'border-border bg-card shadow-[var(--shadow-card)]' : 'border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.018))] shadow-[0_24px_48px_-28px_rgba(0,0,0,0.8)]'} ${className}`}
    >
      {!compact && (
        <div className="mb-3 flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/[0.12] text-accent ring-1 ring-inset ring-accent/25">
            <Share2 className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold leading-tight">{t('reel.share.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('reel.share.lead')}</p>
          </div>
        </div>
      )}

      <div className={`flex min-w-0 items-center gap-2 rounded-xl border px-3 py-2 font-mono text-xs text-muted-foreground ${light ? 'border-border bg-background' : 'border-white/10 bg-black/30'}`}>
        <span className="truncate" data-testid="share-card-link">{link}</span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
        <button type="button" onClick={() => void share()} disabled={busy} className={`${btn} col-span-2 sm:col-span-1`} data-testid="share-card-share">
          <Share2 className="h-4 w-4" aria-hidden />
          {t('reel.share.share')}
        </button>
        <button type="button" onClick={() => void copy()} className={btn} data-testid="share-card-copy">
          {copied ? <Check className="h-4 w-4 text-accent" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
          {copied ? t('reel.share.copied') : t('reel.share.copy')}
        </button>
        <a href={whatsapp} target="_blank" rel="noopener noreferrer" className={btn} data-testid="share-card-whatsapp">
          <MessageCircle className="h-4 w-4" aria-hidden />
          WhatsApp
        </a>
      </div>

      {credits > 0 && (
        <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground" data-testid="share-card-referral">
          <Gift className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
          {isAuthenticated ? (
            <span>{t('reel.share.referral', { credits })}</span>
          ) : (
            <span>
              {t('reel.share.referralSignedOut', { credits })}{' '}
              <a
                href={`/login?redirect_to=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')}`}
                className="font-semibold text-accent underline-offset-4 hover:underline"
              >
                {t('reel.share.signIn')}
              </a>
            </span>
          )}
        </p>
      )}
    </section>
  );
}

export default ShareCard;
